import { useEffect, useRef } from 'react'
import { rbacAPI } from '../services/api'
import { hasAnyPermission, collectRequireAnyPermissions, findUnknownPermissions } from '../utils/permissions'

// RBAC Slice 3 — catalog cross-check (the "checked against the catalog's
// resource/permission model" half of the de-hardcode work).
//
// Nav/header visibility is (and always was, correctly) gated by the user's
// LIVE effective permissions from /api/auth/me, not a hardcoded role table
// — see utils/permissions.js. What COULD still drift is the permission-name
// STRING each nav item declares (`requireAny`): those are RBAC data, seeded
// via migrations and editable at the permission_name level only through a
// schema change, not the Roles console (which only reassigns existing
// permissions to roles/users) — but a rename/removal is still possible
// between a backend deploy and a frontend deploy, or a future migration.
// If that ever happens, the affected nav item silently becomes unreachable
// for EVERYONE, super_admin included, with no error anywhere.
//
// This hook is that guard: once per session, for users who can actually
// read the full catalog, it fetches the live permission set and
// console.warns if any hardcoded requireAny string no longer matches a
// real permission_name. It is diagnostic only — it never blocks rendering,
// never throws into the caller, and never changes what's shown; the actual
// gate stays hasAnyPermission() against /me's effective permissions.
//
// Why "for users who can actually read the full catalog" and not everyone:
// GET /api/auth/rbac/catalog is manage_users-gated server-side (see
// services/auth/routes/rbac.js) — a non-admin calling it would just 403.
// That population is also exactly who's positioned to notice and fix a
// drifted permission string (they're the ones who can already reach the
// Roles & Permissions console), so gating the check itself on manage_users
// costs nothing real.
export default function useRbacDriftCheck(user, navItemGroups) {
  const checkedRef = useRef(false)

  // Tracks true component-unmount only (via the [] effect below), separate
  // from this hook's other effect re-running when `user`/`navItemGroups`
  // change. Callers (Header) pass `navItemGroups` as a fresh array literal
  // ([NAV_ITEMS, ACCOUNT_LINKS]) on every render, so an effect keyed on
  // [user, navItemGroups] re-fires — and, critically, RUNS ITS CLEANUP —
  // on every Header re-render (route change, search open/close, etc.), not
  // just when something meaningful changed. A `cancelled` flag reset by
  // that cleanup would silently discard the in-flight one-shot catalog
  // fetch the moment Header re-rendered before the network response
  // arrived, so the diagnostic could go dark for the entire session. Guard
  // against real unmount here instead, independent of that per-render churn.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (checkedRef.current) return
    if (!hasAnyPermission(user, ['manage_users'])) return

    checkedRef.current = true

    ;(async () => {
      try {
        const catalog = await rbacAPI.getCatalog()
        if (!mountedRef.current) return

        const known = (catalog?.permissions || []).map((p) => p.permission_name)
        const referenced = collectRequireAnyPermissions((navItemGroups || []).flat())
        const unknown = findUnknownPermissions(referenced, known)

        if (unknown.length > 0) {
          console.warn(
            '[rbac] nav/header gating references permission(s) not found in the live RBAC catalog — ' +
              'these nav items can never be shown to anyone (including super_admin) until this is fixed:',
            unknown
          )
        }
      } catch (err) {
        if (!mountedRef.current) return
        // Diagnostic only — a failed/forbidden catalog fetch must never
        // surface to the user or affect header/sidebar rendering.
        console.warn('[rbac] drift check could not load the RBAC catalog:', err?.message || err)
      }
    })()
  }, [user, navItemGroups])
}
